// ─── Prenotare un'ora, e spostarla ───────────────────────────────────────────
//
// 🚨⭐⭐ **NON LA GRIGLIA DELLA SEGRETERIA**, ed è una misura: quella è larga
// `4.5rem + N×min 7rem` (⇒ `Griglia.tsx`), cioè **520 px minimi** con quattro
// campi contro i **390** di un telefono — si scorrerebbe in orizzontale per
// leggere un'ora.
// ⚖️ E non è solo larghezza: quella griglia serve a **confrontare i campi fra
// loro**, che è il mestiere di chi incastra la giornata del circolo. Al maestro
// serve **una** ora libera dove sta lui.
// 🔄⭐ **DAL 23/09 (voce 14) LE ORE SONO UNA GRIGLIA DI ORE, non un elenco di
// fasce per campo.** 🗣️ *«Così è proprio brutto utilizzarlo da cellulare, trova
// una maniera più bella e facile»*, guardando una fascia aperta con sette
// bottoni neri lunghi quanto lo schermo. ⇒ Quattro ore per riga, divise in
// mattina, pomeriggio e sera; il campo si sceglie **dopo**, nel foglio, dove è
// già scelto il primo libero (⇒ `orari.ts`, mockup
// `mockup/maestro-scelta-ora-mockup.html`). Questa riga diceva «un elenco»: è
// corretta, non affiancata.
//
// ⭐ **La stessa schermata serve a prenotare e a spostare**, e cambia due cose:
// cosa succede quando si tocca uno spazio, e il fatto che spostando la lezione
// che si muove **non conta come occupata** (`escludi`). Due schermate gemelle
// diverrebbero diverse al primo ritocco, e quella dimenticata sarebbe quella
// che si usa meno — cioè lo spostamento, che è il gesto fatto di fretta.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DURATA_DEL_TIPO,
  type FasciaDelMioGiorno,
  type MiaLezione,
  type SpazioDelMaestro,
  type TipoDelMaestro,
} from '@pmo/shared';
import { ScegliDurata, scriviDurata } from './Durata.js';
import { FoglioBasso } from './FoglioBasso.js';
import { MOMENTI, fineDi, leMieDelGiorno, momentoDi, orariLiberi, type OrarioLibero } from './orari.js';

/**
 * Lo spazio scelto, col suo **giorno**.
 *
 * ⚠️ `SpazioDelMaestro` il giorno non ce l'ha, ed è giusto così: nella risposta
 * viaggia una volta sola, in testa all'elenco (⇒ `mieiSpaziSchema`), invece che
 * ripetuto su ognuna delle sue righe. A rimetterlo insieme è questa schermata,
 * che il giorno lo sta già mostrando.
 */
type Scelta = { courtId: string; date: string; time: string; durationMinutes: number };

/**
 * ⭐⭐ **IL GESTO CHE STA PER ESSERE FATTO, detto a parole** — voce 12
 * (21/09/2026), dal mockup `mockup/prenota-maestro-mockup.html`.
 *
 * 🗣️ Da una sua segnalazione: *«quando imposto una lezione non c'ho il bottone
 * per prenotarla, quello per annullarla»*. 📏 Misurato: la fascia si apriva e
 * compariva un bottone con dentro **un'ora nuda**, che prenotava all'istante —
 * e si atterrava su una scheda il cui unico comando era «Annulla la lezione».
 * ⇒ Il verbo per **disfare** c'era, quello per **fare** no.
 * 📌 *Un gesto reversibile può permettersi di non spiegarsi; quello che occupa
 *    un campo no.*
 *
 * ⛔⭐⭐ **ED È FACOLTATIVO, che è la parte da non sbagliare.** Questa schermata
 * la usano **tre** cose: prenotare, spostare, e scegliere quando si fa un
 * **corso** (⇒ `Corsi.tsx`). Nel corso il tocco **non scrive niente** — porta
 * al passo dove si scrive il nome e quante volte, e lì la conferma c'è già.
 * ⇒ Chi non passa un `conferma` tiene il comportamento di prima, ed è giusto:
 *   chiederlo anche lì vorrebbe dire domandare «prenoti quest'ora?» per un
 *   gesto che non prenota, e poi domandarlo un'altra volta.
 * 📌 *Una conferma messa a tappeto non è più prudente: è una domanda fatta
 *    anche a chi non sta decidendo niente — e una domanda che si può ignorare
 *    la si impara a ignorare.*
 */
export interface GestoDaConfermare {
  /** Il titolo del foglio: «Prenoti quest'ora?». */
  domanda: string;
  /**
   * Il bottone che fa la cosa, nel foglio: «Prenota alle 15:00» · «Sposta alle
   * 15:00». 🔄 Dal 23/09 (voce 14) sta **solo** lì: l'ora nella griglia è una
   * scelta, e il gesto si nomina dove lo si fa.
   */
  sulBottone: (ora: string) => string;
  /**
   * 🚚 Dov'è **adesso** la cosa che si sta spostando. Solo spostando: senza,
   * si sposta senza sapere cosa si lascia.
   */
  adesso?: string;
  /**
   * 📝 Una riga che dice cosa succede **dopo** il bottone, quando non è ovvio.
   * Serve allo spostamento dal 23/09 (voce 17): lì il bottone non sposta niente
   * — riporta alla scheda, dove si conferma tutto insieme — e senza dirlo si
   * crederebbe il contrario.
   */
  regola?: React.ReactNode;
  /**
   * 🧹 Il rifiuto di prima si butta via chiudendo il foglio, o resterebbe
   * scritto sopra un'ora che non è più quella che l'ha causato.
   */
  azzeraErrore?: () => void;
}
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { giornoDellaSettimana, giornoPerEsteso, giornoSenzaAnno, oggi, piuGiorni } from '../../lib/time.js';
import { ETICHETTA_TIPO, rinfrescaLaGiornata, tacca } from './pezzi.js';
import { conCambio, leggiBozza, pulisci, scriviBozza, useBozza, vista } from './bozza.js';

/**
 * ⭐⭐ **PRENOTARE È UN GESTO IN TRE PASSI: cosa → quando → i dettagli.**
 *
 * 🗣️ 19/09/2026: *«mi deve aprire una schermata più semplice di quella attuale,
 * dove io decido se prenotare una lezione o un corso, e mi deve far vedere le
 * disponibilità che ho»*.
 *
 * 🔄⭐ **DAL 23/09 (voce 13) IL PRIMO PASSO NON È PIÙ UNA SCHERMATA: è il foglio
 * del «+» nell'agenda** (⇒ `Agenda.tsx`, `CosaPrenoti`). La barra in basso non
 * c'è più, e la domanda «lezione o corso?» si fa dove si tocca il «+» — col
 * mockup approvato del 20/09 che l'aveva già disegnata così.
 * ⇒ Questo indirizzo è il **secondo** passo della lezione: si arriva qui già
 *    sapendo cosa si prenota, e si torna all'agenda.
 * ⛔ Il terzo passo del corso — il nome, quante volte — resta la schermata che
 *    esiste già (⇒ `Corsi.tsx`).
 */
export function Prenota(): React.JSX.Element {
  /* 🏋️ Voce 21: il «+» dice cosa si prenota nell'indirizzo. Tutto ciò che non è
     «allenamento» resta una lezione, com'era prima. */
  const [cerca] = useSearchParams();
  const tipo: TipoDelMaestro = cerca.get('tipo') === 'allenamento' ? 'allenamento' : 'lezione';
  return <UnOraPerMe key={tipo} tipo={tipo} />;
}

function UnOraPerMe({ tipo }: { tipo: TipoDelMaestro }): React.JSX.Element {
  const vaiA = useNavigate();
  const coda = useQueryClient();
  const allenamento = tipo === 'allenamento';

  const crea = useMutation({
    meta: { fatto: 'lezione prenotata' },
    mutationFn: (s: Scelta) =>
      chiama<MiaLezione>('POST', '/maestro/lezioni', {
        courtId: s.courtId,
        date: s.date,
        time: s.time,
        durationMinutes: s.durationMinutes,
        tipo,
      }),
    onSuccess: (l) => {
      rinfrescaLaGiornata(coda);
      /* ⭐ Si finisce sulla **scheda della lezione**, non sull'elenco: il gesto
         dopo aver preso un'ora è quasi sempre metterci gli allievi, e quella è
         la schermata che lo fa (tappa ③).
         ⭐ E si porta con sé **che cosa è appena successo**: la scheda lo dice
            in cima, o l'unica cosa che si legge arrivando è «Annulla la
            lezione» (⇒ voce 12). */
      vaiA(`/maestro/lezioni/${l.id}`, { state: { appena: 'prenotata' } });
    },
  });

  return (
    <SceltaDellOra
      titolo={allenamento ? 'Quando si fa l’allenamento' : 'Quando'}
      tornaA="/maestro"
      tornaAChe="La mia agenda"
      /* ⏱️ Voce 22: anche la lezione dura quanto dice il tipo — un'ora. */
      durataFissa={{ minuti: DURATA_DEL_TIPO[tipo], cosa: allenamento ? 'Allenamento' : 'Lezione' }}
      inCorso={crea.isPending}
      errore={crea.error}
      scegli={(s) => crea.mutate(s)}
      conferma={{
        domanda: allenamento ? 'Prenoti l’allenamento?' : 'Prenoti quest’ora?',
        sulBottone: (ora) => `Prenota alle ${ora}`,
        azzeraErrore: () => crea.reset(),
      }}
    />
  );
}

export function Sposta(): React.JSX.Element {
  const { id = '' } = useParams();
  const vaiA = useNavigate();

  /**
   * 🚨⭐ **SI APRE SUL GIORNO DELLA LEZIONE, non su oggi — misurato addosso il
   *    18/09/2026.** Aprendosi su oggi, chi toccava uno spazio senza accorgersi
   *    del giorno **spostava la lezione a oggi**: nessun errore, nessun avviso,
   *    e la lezione di venerdì prossimo finita stasera. (E se l'ora di oggi era
   *    già passata, da lì non si poteva più né spostare né annullare.)
   * 📌 *Una schermata che arriva da una cosa deve partire da dove quella cosa
   *    sta: il valore predefinito è una risposta data al posto di chi guarda.*
   * 🔄 Dal 23/09 (voce 17) «dove quella cosa sta» è la lezione **con sopra le
   *    modifiche non ancora confermate**: chi ha appena cambiato la durata e
   *    poi apre l'ora deve trovare le ore della durata nuova.
   */
  const lezione = useQuery({
    queryKey: ['maestro', 'lezione', id],
    queryFn: () => chiama<MiaLezione>('GET', `/maestro/lezioni/${id}`),
  });
  const bozza = useBozza(id);
  const dallaScheda = (useLocation().state as { daScheda?: boolean } | null)?.daScheda === true;

  if (lezione.isLoading) return <Attesa cosa="la lezione" />;
  if (lezione.error) return <Guasto errore={lezione.error} />;
  const l = lezione.data!;
  const w = vista(l, pulisci(l, bozza));

  return (
    <SceltaDellOra
      titolo={l.type === 'allenamento' ? 'Sposta l’allenamento' : 'Sposta la lezione'}
      /* ⏳ Voce 22: spostando si tiene la durata che ha — anche quella di una
         lezione nata da un'ora e mezza prima della regola, che non si accorcia
         da sola (⇒ `maestro/tipo.ts`). */
      durataFissa={
        l.type === 'lezione' || l.type === 'allenamento'
          ? {
              minuti: w.durationMinutes,
              cosa: ETICHETTA_TIPO[l.type] ?? l.type,
              comeAdesso: w.durationMinutes !== DURATA_DEL_TIPO[l.type],
            }
          : undefined
      }
      /* 🔁 La lezione che si sta spostando non conta come occupata: se no
         l'unico spazio che non comparirebbe sarebbe quello in cui sta adesso. */
      escludi={id}
      tornaAChe="Torna alla lezione"
      giornoIniziale={w.date}
      durataIniziale={w.durationMinutes}
      tornaA={`/maestro/lezioni/${id}`}
      inCorso={false}
      errore={null}
      /* ✏️⭐⭐ **NON SCRIVE NIENTE — voce 17, 23/09/2026.** L'ora scelta entra
         nella bozza della lezione e si torna alla scheda, dove si conferma
         tutto insieme: giorno, ora, durata, campo e nota, con **un** avviso
         solo. Prima spostava subito, e cambiare ora e poi campo voleva dire
         due messaggi sullo stesso telefono. */
      scegli={(s, courtName) => {
        scriviBozza(
          id,
          conCambio(l, leggiBozza(id), {
            date: s.date,
            time: s.time,
            durationMinutes: s.durationMinutes,
            campo: { courtId: s.courtId, courtName },
          }),
        );
        /* 🔙 Si torna **indietro**, non avanti: arrivati dalla scheda, un
           collegamento nuovo lascerebbe nella storia la scheda due volte, e il
           «indietro» del telefono sembrerebbe non fare niente. Chi invece è
           arrivato qui da un indirizzo scritto a mano la scheda dietro non ce
           l'ha, e ci va. */
        if (dallaScheda) vaiA(-1);
        else vaiA(`/maestro/lezioni/${id}`, { replace: true });
      }}
      /* 🚚 Il verbo è un ALTRO, e non è pignoleria: «Prenota» su uno
         spostamento farebbe credere di star prendendo un'ora **in più**
         invece di muovere quella che si ha già. E si dice **da dove** si
         viene, o si sposta senza sapere cosa si lascia. */
      conferma={{
        domanda: 'Usi quest’ora?',
        sulBottone: () => 'Usa quest’ora',
        adesso: `${giornoPerEsteso(l.date)}, ${l.time}, ${l.courtName}`,
        regola: (
          <>
            Non si sposta ancora niente: la confermi <strong>nella scheda</strong>, insieme alle altre
            modifiche.
          </>
        ),
      }}
    />
  );
}

/**
 * ⭐ Scegliere **un'ora libera** fra le sue: la usano tutte e tre le cose che
 * cominciano da un'ora — prenotare una lezione, spostarla, e aprire un corso
 * (⇒ `Corsi.tsx`). Cambia cosa succede quando si tocca uno spazio, non come lo
 * si sceglie: una seconda copia divergerebbe al primo ritocco, e la dimenticata
 * sarebbe quella usata di rado.
 */
export function SceltaDellOra({
  titolo,
  escludi,
  tornaA,
  indietro,
  tornaAChe,
  giornoIniziale,
  durataIniziale,
  durataFissa,
  scegli,
  conferma,
  inCorso,
  errore,
}: {
  titolo: string;
  escludi?: string;
  tornaA?: string;
  /**
   * 🔙 Tornare indietro **senza cambiare indirizzo**: serve quando il passo di
   * prima è uno stato di questa stessa schermata (⇒ `Prenota`), e allora un
   * collegamento farebbe uscire e rientrare perdendo la scelta.
   */
  indietro?: () => void;
  /** Cosa c'è dietro il «‹»: scritto, non indovinato dalla schermata. */
  tornaAChe?: string;
  giornoIniziale?: string;
  durataIniziale?: number;
  /**
   * 🏋️ Voce 21: quando la durata la dice il tipo, non si sceglie — si legge.
   * `cosa` è il nome del tipo, che il riepilogo del foglio scrive accanto.
   */
  durataFissa?: { minuti: number; cosa: string; comeAdesso?: boolean };
  /**
   * Il secondo argomento è il **nome** del campo scelto: lo usa solo lo
   * spostamento, che non scrive niente e deve poter dire «Campo 3» nella scheda
   * senza andarlo a cercare (⇒ `bozza.ts`). Resta fuori da `Scelta` apposta:
   * quella viaggia intera nel corpo della richiesta del corso.
   */
  scegli: (s: Scelta, courtName: string) => void;
  /** ⛔ Assente = nessuna domanda: si sceglie il campo e si va avanti (⇒ `GestoDaConfermare`, il corso). */
  conferma?: GestoDaConfermare;
  inCorso: boolean;
  errore: unknown;
}): React.JSX.Element {
  const [giorno, setGiorno] = useState(giornoIniziale ?? oggi());
  /* ⭐ Spostando si parte dalla durata che la lezione ha già: quasi sempre si
     sposta l'ora, non la lunghezza. Se non è fra quelle proposte si mostra
     com'è — è la sua, non un numero da correggere di nascosto. */
  const [durata, setDurata] = useState(durataFissa?.minuti ?? durataIniziale ?? 60);

  /**
   * ⭐ L'ora toccata e il campo scelto, in attesa che il bottone venga premuto.
   * `null` = il foglio non c'è. Finché sta qui **non è successo niente**.
   */
  const [daConfermare, setDaConfermare] = useState<{ time: string; courtId: string } | null>(null);

  /* 🧹 Cambiando giorno o durata, un foglio aperto parlerebbe di un'ora che
     non si sta più guardando. */
  useEffect(() => setDaConfermare(null), [giorno, durata]);

  const chiudiIlFoglio = (): void => {
    setDaConfermare(null);
    conferma?.azzeraErrore?.();
  };

  const spazi = useQuery({
    queryKey: ['maestro', 'spazi', giorno, durata, escludi ?? null],
    queryFn: () =>
      chiama<{
        perimetroAperto: boolean;
        via: boolean;
        items: Array<Omit<SpazioDelMaestro, 'date'>>;
        fasce: FasciaDelMioGiorno[];
      }>(
        'GET',
        `/maestro/spazi?date=${giorno}&durationMinutes=${durata}` + (escludi ? `&escludi=${escludi}` : ''),
      ),
  });

  const fasce = spazi.data?.fasce;
  const orari = useMemo(() => orariLiberi(fasce ?? [], durata), [fasce, durata]);
  const mie = useMemo(() => leMieDelGiorno(fasce ?? []), [fasce]);
  const toccato = daConfermare === null ? undefined : orari.find((o) => o.time === daConfermare.time);

  /**
   * ⭐ Il tocco su un'ora. Con una domanda da fare apre il foglio, col primo
   * campo già scelto. Senza (il corso) non c'è niente da confermare: se il
   * campo è uno solo si va avanti subito, se sono di più il foglio chiede solo
   * **quale** — una scelta, non una conferma.
   */
  const tocca = (o: OrarioLibero): void => {
    if (conferma === undefined && o.campi.length === 1) {
      scegli(
        { courtId: o.campi[0]!.courtId, date: giorno, time: o.time, durationMinutes: durata },
        o.campi[0]!.courtName,
      );
      return;
    }
    setDaConfermare({ time: o.time, courtId: o.campi[0]!.courtId });
  };

  return (
    <div className="scelta-ora">
      {tornaA !== undefined ? (
        <p>
          <Link className="indietro" to={tornaA}>
            ‹ {tornaAChe ?? 'Torna alla lezione'}
          </Link>
        </p>
      ) : indietro !== undefined ? (
        <p>
          <button type="button" className="indietro piatto" onClick={indietro}>
            ‹ {tornaAChe ?? 'Indietro'}
          </button>
        </p>
      ) : null}
      <h2 className="titolo-maestro">{titolo}</h2>

      <StrisciaDeiGiorni giorno={giorno} scegli={setGiorno} dal={giornoIniziale} />
      <p className="che-giorno-e">
        <strong>
          {giorno === oggi() ? 'Oggi, ' : giorno === piuGiorni(oggi(), 1) ? 'Domani, ' : ''}
          {giornoDellaSettimana(giorno)} {giornoSenzaAnno(giorno)}
        </strong>
      </p>

      {durataFissa ? (
        <p className="durata-del-tipo">
          Dura <strong>{scriviDurata(durataFissa.minuti)}</strong>
          {durataFissa.comeAdesso === true ? ', come adesso.' : ': la dice il tipo.'}
        </p>
      ) : (
        <ScegliDurata durata={durata} scegli={setDurata} />
      )}

      {/* 👀 Le sue lezioni del giorno in una riga: la giornata si capisce senza
          che le ore occupate riempiano lo schermo. */}
      {mie.length > 0 && (
        <p className="gia-tue">
          Quel giorno hai già:{' '}
          {mie.map((m, i) => (
            <Fragment key={m.lezioneId}>
              {i > 0 && ' · '}
              <strong>
                {m.dalle}–{m.alle}
              </strong>{' '}
              {m.courtName}
            </Fragment>
          ))}
        </p>
      )}

      {spazi.isLoading ? (
        <Attesa cosa="le tue ore" />
      ) : spazi.error ? (
        <Guasto errore={spazi.error} />
      ) : orari.length === 0 ? (
        /* ⚠️ **QUATTRO VUOTI DIVERSI, e chiedono quattro cose diverse**: se il
           circolo non ti ha aperto niente quel giorno si telefona alla
           segreteria, che è l'unica che può cambiarlo; se le tue ore ci sono ma
           sono passate si guarda domani; se ci sono e sono piene, un altro
           giorno.
           🏖️ **E il primo è «ci sei tu che hai detto di non esserci»** (tappa
              ⑥): quel giorno anche `perimetroAperto` è falso, per costruzione,
              e senza questa riga il vuoto arriverebbe travestito da quello del
              circolo. ⇒ Si legge **per primo**.
           📌 *Un elenco vuoto senza il suo perché è una schermata che sembra
              rotta — e un perché sbagliato manda a cercare una causa che non
              c'è.* */
        <>
          <p className="tenue">
            {spazi.data!.via
              ? 'Quel giorno hai scritto che non ci sei.'
              : !spazi.data!.perimetroAperto
                ? 'Questo giorno il circolo non ti ha aperto nessuna ora: lo decide la segreteria.'
                : giorno <= oggi()
                  ? 'Di questo giorno non resta più niente: le tue ore sono già passate.'
                  : 'Le tue ore di questo giorno sono tutte occupate. Prova un altro giorno.'}
          </p>
          {spazi.data!.via && (
            <p className="regola">
              Se sei tornato disponibile, toglilo da <Link to="/maestro/assenze">«Quando non ci sono»</Link> e
              le tue ore tornano.
            </p>
          )}
        </>
      ) : (
        MOMENTI.map((momento) => {
          const qui = orari.filter((o) => momentoDi(o.time) === momento);
          if (qui.length === 0) return null;
          return (
            <section key={momento} className="momento" aria-label={momento}>
              <h3>
                {momento}{' '}
                <span className="tenue">
                  {qui.length === 1 ? '1 orario libero' : `${qui.length} orari liberi`}
                </span>
              </h3>
              <div className="orari">
                {qui.map((o) => {
                  /* ⭐ Sotto l'ora, quanti campi: dice se c'è scelta prima di
                     toccare. «impegnato» vince, perché è l'unica cosa che
                     cambia la decisione. */
                  const sotto =
                    o.accavallata !== null
                      ? 'impegnato'
                      : o.campi.length === 1
                        ? '1 campo'
                        : `${o.campi.length} campi`;
                  return (
                    <button
                      key={o.time}
                      type="button"
                      className={o.accavallata !== null ? 'impegnato' : undefined}
                      aria-label={`${o.time}, ${sotto}`}
                      disabled={inCorso}
                      onClick={() => tocca(o)}
                    >
                      <span className="alle">{o.time}</span>
                      <span className="quanti-campi">{sotto}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {/* ⚠️ Il rifiuto si legge in fondo, dove si è appena toccato. ⛔ E **solo
          quando non c'è il foglio**: col foglio aperto riguarda l'ora che sta
          dentro, e si legge lì — qui resterebbe coperto dal velo. */}
      {daConfermare === null && <Guasto errore={errore} />}

      {/* ⭐⭐ IL FOGLIO: l'ora, il campo già scelto, e il bottone che fa la cosa.
          Finché nessuno l'ha premuto **non è successo niente**. */}
      {daConfermare !== null && toccato !== undefined && (
        <FoglioBasso titolo={conferma?.domanda ?? 'Su quale campo?'} chiudi={chiudiIlFoglio}>
          <div className="foglio-ora">
            {/* ⛔ Il rifiuto sta DENTRO il foglio, che resta aperto: chi ha
                appena premuto guarda qui. */}
            <Guasto errore={errore} />
            {conferma?.adesso !== undefined && <p className="da-dove">Adesso è {conferma.adesso}.</p>}
            <p className="riepilogo-ora">
              <span className="grosso">
                {toccato.time} – {fineDi(toccato.time, durata)}
              </span>
              <span className="riga-riep">
                {giornoPerEsteso(giorno)} ·{' '}
                {durataFissa ? `${durataFissa.cosa}, ${scriviDurata(durata)}` : `${durata} minuti`}
              </span>
            </p>

            {conferma === undefined ? (
              /* Il corso: il campo si tocca e si va avanti. La conferma vera
                 arriva al passo dopo, col nome e le volte. */
              <div className="campi-foglio" role="group" aria-label="Il campo">
                {toccato.campi.map((c) => (
                  <button
                    key={c.courtId}
                    type="button"
                    onClick={() =>
                      scegli(
                        {
                          courtId: c.courtId,
                          date: giorno,
                          time: toccato.time,
                          durationMinutes: durata,
                        },
                        c.courtName,
                      )
                    }
                  >
                    {c.courtName}
                  </button>
                ))}
              </div>
            ) : toccato.campi.length === 1 ? (
              <p className="quale-campo">
                Campo: <strong>{toccato.campi[0]!.courtName}</strong> (l’unico libero a quest’ora)
              </p>
            ) : (
              <>
                <p className="quale-campo">Su quale campo</p>
                <div className="campi-foglio" role="radiogroup" aria-label="Il campo">
                  {toccato.campi.map((c) => (
                    <button
                      key={c.courtId}
                      type="button"
                      role="radio"
                      aria-checked={c.courtId === daConfermare.courtId}
                      className={c.courtId === daConfermare.courtId ? 'scelto' : undefined}
                      disabled={inCorso}
                      onClick={() => setDaConfermare({ time: toccato.time, courtId: c.courtId })}
                    >
                      {c.courtName}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* ⚠️ Non si toglie un'ora che si accavalla: si DICE. Può essere una
                cosa voluta, ma non la si deve scoprire dopo. */}
            {toccato.accavallata !== null && (
              <p className="avviso-tua">
                A quest’ora hai già una lezione: {toccato.accavallata.dalle}–{toccato.accavallata.alle},{' '}
                {toccato.accavallata.courtName}.
              </p>
            )}

            {conferma?.regola !== undefined && <p className="regola">{conferma.regola}</p>}

            <p className="azioni-foglio">
              {/* ⛔ Quando l'ora se l'è presa qualcun altro il bottone che fa la
                  cosa NON c'è: lasciarlo inviterebbe a ripremere una cosa che
                  non può riuscire. Resta la strada che porta fuori. */}
              {conferma !== undefined && errore === null && (
                <button
                  type="button"
                  className="azione-principale"
                  disabled={inCorso}
                  onClick={() =>
                    scegli(
                      {
                        courtId: daConfermare.courtId,
                        date: giorno,
                        time: toccato.time,
                        durationMinutes: durata,
                      },
                      toccato.campi.find((c) => c.courtId === daConfermare.courtId)?.courtName ?? '',
                    )
                  }
                >
                  {inCorso ? 'Un attimo…' : conferma.sulBottone(toccato.time)}
                </button>
              )}
              <button type="button" className="piatto" onClick={chiudiIlFoglio}>
                Scegli un’altra ora
              </button>
            </p>
          </div>
        </FoglioBasso>
      )}
    </div>
  );
}

/**
 * 📅 I giorni in striscia, come nell'agenda: un tocco invece di tante frecce.
 *
 * ⚖️ Da oggi a **otto settimane**: si prenota avanti, non indietro. Spostando
 * una lezione lontana la striscia arriva fino a lei, e due settimane oltre.
 * ⛔ Niente «quanto è pieno» sotto le tacche: per saperlo servirebbe una
 *    domanda al server per ogni giorno, e sessanta domande per aprire una
 *    schermata non valgono un'etichetta.
 */
function StrisciaDeiGiorni({
  giorno,
  scegli,
  dal,
}: {
  giorno: string;
  scegli: (g: string) => void;
  dal?: string;
}): React.JSX.Element {
  const primo = dal !== undefined && dal < oggi() ? dal : oggi();
  const ultimo = [piuGiorni(oggi(), 55), dal === undefined ? '' : piuGiorni(dal, 14)].sort().pop()!;
  const giorni: string[] = [];
  for (let g = primo; g <= ultimo; g = piuGiorni(g, 1)) giorni.push(g);

  /* 🎯 Il giorno scelto si porta in mezzo alla striscia, anche all'apertura:
     spostando una lezione di fra tre settimane, la sua tacca sarebbe fuori
     schermo. Si muove **solo** la striscia, non la pagina. */
  const striscia = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const s = striscia.current;
    const b = s?.querySelector<HTMLElement>('button.scelto');
    if (s && b) s.scrollLeft = b.offsetLeft - (s.clientWidth - b.clientWidth) / 2;
  }, [giorno]);

  return (
    <div className="datario-maestro">
      <div className="tacche" role="tablist" aria-label="I giorni" ref={striscia}>
        {giorni.map((g) => {
          const t = tacca(g);
          return (
            <button
              key={g}
              type="button"
              role="tab"
              aria-selected={g === giorno}
              className={[g === oggi() ? 'oggi' : '', g === giorno ? 'scelto' : ''].filter(Boolean).join(' ')}
              onClick={() => scegli(g)}
            >
              <span className="che-giorno">{g === oggi() ? 'oggi' : t.giorno}</span>
              <span className="che-numero">{t.numero}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
